import React, { useCallback, useEffect, useLayoutEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import useEmblaCarousel from "embla-carousel-react";
import Autoplay from "embla-carousel-autoplay";
import { motion } from "framer-motion";
import { ArrowUpRight, ChevronLeft, ChevronRight } from "lucide-react";
import { ROUTES } from "@/lib/routes";

const TWEEN_FACTOR = 0.52;

function normalizeDiff(diff) {
  if (diff > 0.5) return diff - 1;
  if (diff < -0.5) return diff + 1;
  return diff;
}

function PortfolioCard({ item, isActive }) {
  const videoRef = useRef(null);
  const videoSrc = item.video?.trim();
  const imageSrc = item.image?.trim();
  const showVideo = Boolean(videoSrc);

  useEffect(() => {
    const el = videoRef.current;
    if (!el || !showVideo) return undefined;

    el.defaultMuted = true;
    el.muted = true;
    el.loop = true;
    el.playsInline = true;
    el.setAttribute("muted", "");
    el.setAttribute("playsinline", "");
    el.setAttribute("webkit-playsinline", "");

    const play = () => {
      el.muted = true;
      const p = el.play();
      if (p && typeof p.catch === "function") p.catch(() => {});
    };

    play();
    el.addEventListener("loadeddata", play);
    el.addEventListener("canplay", play);
    el.addEventListener("loadedmetadata", play);

    const onVisibility = () => {
      if (document.visibilityState === "visible") play();
    };
    document.addEventListener("visibilitychange", onVisibility);

    const intervalId = window.setInterval(play, 700);
    const timeoutId = window.setTimeout(() => window.clearInterval(intervalId), 5000);

    return () => {
      el.removeEventListener("loadeddata", play);
      el.removeEventListener("canplay", play);
      el.removeEventListener("loadedmetadata", play);
      document.removeEventListener("visibilitychange", onVisibility);
      window.clearInterval(intervalId);
      window.clearTimeout(timeoutId);
    };
  }, [showVideo, videoSrc]);

  return (
    <div className="relative mx-auto w-full max-w-[240px] sm:max-w-[260px] md:max-w-[280px] lg:max-w-[300px]">
      <div
        className={`relative aspect-[9/16] w-full overflow-hidden rounded-[1.35rem] bg-[#1a0f0d] sm:rounded-[1.5rem] ${
          isActive
            ? "shadow-[0_28px_64px_-18px_rgba(62,39,35,0.5)] ring-1 ring-[#3e2723]/15"
            : "shadow-[0_16px_40px_-20px_rgba(62,39,35,0.3)] ring-1 ring-[#3e2723]/8"
        }`}
      >
        {showVideo ? (
          <video
            key={videoSrc}
            ref={videoRef}
            src={videoSrc}
            poster={imageSrc || undefined}
            muted
            loop
            playsInline
            autoPlay
            preload="auto"
            className="absolute inset-0 h-full w-full object-cover"
          />
        ) : imageSrc ? (
          <img
            src={imageSrc}
            alt={item.imageAlt?.trim() || item.name || ""}
            className="absolute inset-0 h-full w-full object-cover"
            loading="lazy"
            draggable={false}
          />
        ) : (
          <div className="absolute inset-0 bg-gradient-to-br from-[#fbd9e5]/45 to-[#3e2723]/18" />
        )}

        <div className="pointer-events-none absolute inset-0 bg-gradient-to-t from-[#1a0f0d]/90 via-[#1a0f0d]/25 to-transparent" />

        <div className={`absolute inset-x-0 bottom-0 p-4 sm:p-5 ${isActive ? "opacity-100" : "opacity-90"}`}>
          {item.service ? (
            <span className="block text-[9px] font-bold uppercase tracking-[0.2em] text-[#fbd9e5]/75 sm:text-[10px]">
              {item.service}
            </span>
          ) : null}
          {item.name ? (
            <p className="mt-1 font-display text-base font-black tracking-tight text-[#fbd9e5] sm:text-lg lg:text-xl">
              {item.name}
            </p>
          ) : null}
        </div>
      </div>
    </div>
  );
}

export default function CurvedPortfolioShowcase({ copy, items = [] }) {
  const galleryRef = useRef(null);
  const autoplayPlugin = useRef(
    Autoplay({ delay: 4500, stopOnInteraction: false, stopOnMouseEnter: true })
  );
  const [emblaRef, emblaApi] = useEmblaCarousel(
    {
      loop: true,
      align: "center",
      containScroll: false,
      skipSnaps: false,
      startIndex: 0,
      dragFree: false,
    },
    [autoplayPlugin.current]
  );
  const [selectedIndex, setSelectedIndex] = useState(0);
  const [canScroll, setCanScroll] = useState(false);

  const kickVideos = useCallback(() => {
    const root = galleryRef.current;
    if (!root) return;
    root.querySelectorAll("video").forEach((v) => {
      v.muted = true;
      v.loop = true;
      const p = v.play();
      if (p && typeof p.catch === "function") p.catch(() => {});
    });
  }, []);

  const onSelect = useCallback(() => {
    if (!emblaApi) return;
    setSelectedIndex(emblaApi.selectedScrollSnap());
    setCanScroll(emblaApi.canScrollPrev() || emblaApi.canScrollNext());
    kickVideos();
  }, [emblaApi, kickVideos]);

  const applyCurve = useCallback(() => {
    if (!emblaApi) return;
    const engine = emblaApi.internalEngine();
    const scrollProgress = emblaApi.scrollProgress();
    const slides = emblaApi.slideNodes();
    const snapList = emblaApi.scrollSnapList();
    const factor = TWEEN_FACTOR * snapList.length;
    const isNarrow = typeof window !== "undefined" && window.matchMedia("(max-width: 640px)").matches;
    const rotateMax = isNarrow ? 18 : 28;

    snapList.forEach((snap, index) => {
      const slide = slides[index];
      if (!slide) return;

      let diffToTarget = snap - scrollProgress;

      if (engine.options.loop) {
        engine.slideLooper.loopPoints.forEach((loopItem) => {
          const target = loopItem.target();
          if (loopItem.index !== index || target === 0) return;
          if (target === -1) diffToTarget = snap - (1 + scrollProgress);
          if (target === 1) diffToTarget = snap + (1 - scrollProgress);
        });
      }

      diffToTarget = normalizeDiff(diffToTarget);
      const tween = 1 - Math.min(Math.abs(diffToTarget * factor), 1);
      const rotateY = diffToTarget * -rotateMax * (snapList.length > 1 ? 1 : 0);
      const scale = (isNarrow ? 0.82 : 0.74) + tween * (isNarrow ? 0.18 : 0.26);
      const opacity = 0.4 + tween * 0.6;

      slide.style.transform = `rotateY(${rotateY}deg) scale(${scale})`;
      slide.style.opacity = `${opacity}`;
      slide.style.zIndex = `${Math.round(tween * 100)}`;
    });
  }, [emblaApi]);

  useLayoutEffect(() => {
    if (!emblaApi) return undefined;
    onSelect();
    applyCurve();
    const id = requestAnimationFrame(() => {
      applyCurve();
      kickVideos();
    });
    return () => cancelAnimationFrame(id);
  }, [emblaApi, onSelect, applyCurve, kickVideos]);

  useEffect(() => {
    if (!emblaApi) return undefined;
    emblaApi.on("select", onSelect);
    emblaApi.on("reInit", onSelect);
    emblaApi.on("scroll", applyCurve);
    emblaApi.on("reInit", applyCurve);
    emblaApi.on("settle", applyCurve);
    emblaApi.on("settle", kickVideos);

    const onResize = () => {
      emblaApi.reInit();
      applyCurve();
      kickVideos();
    };
    window.addEventListener("resize", onResize);

    return () => {
      emblaApi.off("select", onSelect);
      emblaApi.off("reInit", onSelect);
      emblaApi.off("scroll", applyCurve);
      emblaApi.off("reInit", applyCurve);
      emblaApi.off("settle", applyCurve);
      emblaApi.off("settle", kickVideos);
      window.removeEventListener("resize", onResize);
    };
  }, [emblaApi, onSelect, applyCurve, kickVideos]);

  useEffect(() => {
    if (!emblaApi || items.length <= 1) return undefined;
    if (window.matchMedia("(prefers-reduced-motion: reduce)").matches) return undefined;
    autoplayPlugin.current?.play?.();
    return undefined;
  }, [emblaApi, items.length]);

  useEffect(() => {
    kickVideos();
    const id = window.setInterval(kickVideos, 900);
    const stop = window.setTimeout(() => window.clearInterval(id), 6000);
    return () => {
      window.clearInterval(id);
      window.clearTimeout(stop);
    };
  }, [kickVideos, items]);

  const pillars = copy.pillars || [];
  const eyebrow = (copy.showcase_eyebrow || copy.eyebrow || "").trim();
  const subtitle = (copy.showcase_subtitle || "").trim();

  return (
    <section
      className="relative overflow-x-clip bg-[#fbfaf9] pb-16 pt-[7.25rem] sm:pb-20 sm:pt-[8.25rem] lg:pb-24 lg:pt-[8.75rem]"
      data-testid="portfolio-showcase"
    >
      <div
        aria-hidden
        className="pointer-events-none absolute inset-0 bg-[radial-gradient(ellipse_at_50%_0%,rgba(251,217,229,0.45)_0%,transparent_55%)]"
      />
      <div
        aria-hidden
        className="pointer-events-none absolute inset-x-0 top-0 h-px bg-gradient-to-r from-transparent via-[#3e2723]/10 to-transparent"
      />

      <div className="relative mx-auto max-w-3xl px-5 text-center sm:px-8">
        {eyebrow ? (
          <motion.p
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.55, ease: [0.16, 1, 0.3, 1] }}
            className="text-[10px] font-semibold uppercase tracking-[0.28em] text-[#3e2723]/50 sm:text-[11px]"
          >
            {eyebrow}
          </motion.p>
        ) : null}

        <motion.h1
          initial={{ opacity: 0, y: 14 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.65, delay: 0.04, ease: [0.16, 1, 0.3, 1] }}
          className={`${eyebrow ? "mt-3 sm:mt-4" : ""} font-display text-[1.55rem] font-black leading-[1.08] tracking-tight text-[#3e2723] sm:text-[2.35rem] lg:text-[2.85rem]`}
        >
          {copy.showcase_title || copy.title}
        </motion.h1>

        {subtitle ? (
          <motion.p
            initial={{ opacity: 0, y: 12 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.08, ease: [0.16, 1, 0.3, 1] }}
            className="mx-auto mt-3 hidden max-w-xl text-[15px] leading-relaxed text-[#7a5c55] sm:mt-4 sm:block sm:text-base"
          >
            {subtitle}
          </motion.p>
        ) : null}

        <motion.div
          initial={{ opacity: 0, y: 12 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.1, ease: [0.16, 1, 0.3, 1] }}
          className="mt-5 flex justify-center sm:mt-6"
        >
          <Link
            to={ROUTES.contact}
            className="group inline-flex items-center rounded-full border border-[#3e2723]/10 bg-white pl-5 pr-1.5 py-1.5 text-[13px] font-semibold text-[#3e2723] shadow-[0_10px_30px_-14px_rgba(62,39,35,0.22)] transition hover:border-[#3e2723]/18 hover:shadow-[0_14px_36px_-14px_rgba(62,39,35,0.28)] sm:pl-6 sm:text-sm"
            data-testid="portfolio-showcase-cta"
          >
            <span>{copy.showcase_cta || copy.cta}</span>
            <span className="ml-3 inline-flex h-9 w-9 items-center justify-center rounded-full bg-[#3e2723] text-[#fbd9e5] transition duration-300 group-hover:bg-[#2a1a17] sm:ml-4">
              <ArrowUpRight className="h-4 w-4 transition duration-300 group-hover:rotate-45" />
            </span>
          </Link>
        </motion.div>
      </div>

      <div className="relative mt-8 sm:mt-10 lg:mt-12" ref={galleryRef}>
        <div className="mx-auto max-w-[100rem] [perspective:1500px] [perspective-origin:50%_40%]">
          <div ref={emblaRef} className="overflow-hidden px-0 pb-4 sm:px-2 sm:pb-6">
            <div className="flex touch-pan-y items-center [transform-style:preserve-3d]">
              {items.map((item, i) => (
                <div
                  key={`${item.name}-${i}`}
                  className="min-w-0 flex-[0_0_72%] px-1.5 will-change-transform [transform-style:preserve-3d] sm:flex-[0_0_42%] sm:px-2 md:flex-[0_0_32%] lg:flex-[0_0_24%] xl:flex-[0_0_20%]"
                  data-testid={`portfolio-item-${i}`}
                  style={{
                    transform: i === 0 ? "scale(1)" : "scale(0.8)",
                    opacity: i === 0 ? 1 : 0.45,
                    zIndex: i === 0 ? 50 : 10,
                  }}
                >
                  <button
                    type="button"
                    onClick={() => emblaApi?.scrollTo(i)}
                    className="block w-full cursor-pointer text-left [backface-visibility:hidden]"
                    aria-label={item.name}
                    aria-current={i === selectedIndex ? "true" : undefined}
                  >
                    <PortfolioCard item={item} isActive={i === selectedIndex} />
                  </button>
                </div>
              ))}
            </div>
          </div>
        </div>

        {items.length > 1 ? (
          <div className="mt-3 flex items-center justify-center gap-3 px-5 sm:mt-5 sm:gap-4">
            {canScroll ? (
              <button
                type="button"
                aria-label="Proiect anterior"
                onClick={() => emblaApi?.scrollPrev()}
                className="hidden h-10 w-10 items-center justify-center rounded-full border border-[#3e2723]/10 bg-white text-[#3e2723] shadow-sm transition hover:border-[#3e2723]/20 hover:bg-[#faf8f8] sm:inline-flex"
              >
                <ChevronLeft className="h-4 w-4" />
              </button>
            ) : null}

            <div className="inline-flex max-w-[min(100%,22rem)] items-center gap-1.5 overflow-x-auto rounded-full border border-[#3e2723]/8 bg-white/90 px-3 py-2.5 shadow-[0_8px_24px_-14px_rgba(62,39,35,0.18)] backdrop-blur-sm [-ms-overflow-style:none] [scrollbar-width:none] sm:gap-2 sm:px-4 [&::-webkit-scrollbar]:hidden">
              {items.map((_, i) => (
                <button
                  key={i}
                  type="button"
                  aria-label={`Proiect ${i + 1}`}
                  aria-current={i === selectedIndex ? "true" : undefined}
                  onClick={() => emblaApi?.scrollTo(i)}
                  className={`shrink-0 rounded-full transition-all duration-300 ${
                    i === selectedIndex
                      ? "h-2 w-7 bg-[#3e2723] sm:h-2.5 sm:w-9"
                      : "h-2 w-2 bg-[#3e2723]/20 hover:bg-[#3e2723]/40 sm:h-2.5 sm:w-2.5"
                  }`}
                />
              ))}
            </div>

            {canScroll ? (
              <button
                type="button"
                aria-label="Proiect urmator"
                onClick={() => emblaApi?.scrollNext()}
                className="hidden h-10 w-10 items-center justify-center rounded-full border border-[#3e2723]/10 bg-white text-[#3e2723] shadow-sm transition hover:border-[#3e2723]/20 hover:bg-[#faf8f8] sm:inline-flex"
              >
                <ChevronRight className="h-4 w-4" />
              </button>
            ) : null}
          </div>
        ) : null}
      </div>

      {pillars.length > 0 ? (
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-40px" }}
          transition={{ duration: 0.75, ease: [0.16, 1, 0.3, 1] }}
          className="relative mx-auto mt-14 max-w-6xl px-5 sm:mt-16 sm:px-8 lg:mt-20"
        >
          <div className="grid grid-cols-2 gap-x-6 gap-y-8 border-t border-[#3e2723]/10 pt-10 sm:gap-x-10 lg:grid-cols-4 lg:gap-8 lg:pt-12">
            {pillars.map((pillar) => (
              <div key={pillar.n} className="text-left">
                <p className="font-display text-sm font-bold tabular-nums text-[#3e2723]/35 sm:text-base">
                  {pillar.n}
                </p>
                <p className="mt-2 text-sm font-semibold leading-snug text-[#3e2723] sm:text-[15px]">
                  {pillar.title}
                </p>
              </div>
            ))}
          </div>
        </motion.div>
      ) : null}
    </section>
  );
}